/**
 * DebateDrawer - Global slide-in panel for ad-hoc debates.
 *
 * Mounted at DashboardLayout level so it persists across page navigation.
 * Three states: closed, open, minimized.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  X,
  ChevronRight,
  MessageSquare,
  Swords,
  Users,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  History,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { type DebateStartParams, type DebateStreamState } from '@/hooks/useDebateStream';
import { useDebateHistory, type DebateSummary } from '@/hooks/useDebateHistory';
import { usePageContext } from '@/contexts/PageContext';
import { TruncatedText } from '@/components/ui/TruncatedText';
import { MarkdownContent } from '@/components/verdict/MarkdownContent';

export type DebateDrawerMode = 'closed' | 'open' | 'minimized';

interface DebateDrawerProps {
  drawerMode: DebateDrawerMode;
  onClose: () => void;
  onMinimize: () => void;
  onOpen: () => void;
  /** Shared debate stream from DashboardLayout (so pill + drawer use the same instance) */
  debateStream: {
    state: DebateStreamState;
    isConnected: boolean;
    isConnecting: boolean;
    startDebate: (params: DebateStartParams) => void;
    disconnect: () => void;
    reset: () => void;
  };
}

type DepthOption = 'fast' | 'deep';
type ModeOption = 'adversarial' | 'multi-perspective';

const DEPTH_INFO: Record<DepthOption, { label: string; agents: number; rounds: number; credits: string; icon: typeof Clock }> = {
  fast: { label: 'Fast', agents: 3, rounds: 2, credits: '~30', icon: Clock },
  deep: { label: 'Deep', agents: 7, rounds: 3, credits: '~200', icon: Users },
};

export function DebateDrawer({ drawerMode, onClose, onMinimize, onOpen: _onOpen, debateStream }: DebateDrawerProps) {
  const [question, setQuestion] = useState('');
  const [depth, setDepth] = useState<DepthOption>('fast');
  const [mode, setMode] = useState<ModeOption>('multi-perspective');
  const [referenceDebateId, setReferenceDebateId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [contextDismissed, setContextDismissed] = useState(false);

  const { context: pageContext } = usePageContext();
  const { state, startDebate, disconnect, reset } = debateStream;
  const { debates, isLoading: historyLoading, fetchDebates } = useDebateHistory();

  const isOpen = drawerMode === 'open';
  const isRunning = state.status !== 'idle' && state.status !== 'done' && state.status !== 'error';
  const isDone = state.status === 'done';
  const showModeToggle = state.topicType === 'financial' || (pageContext?.pageType === 'stock-analysis');

  // Fetch history when drawer opens
  useEffect(() => {
    if (isOpen && showHistory) {
      fetchDebates();
    }
  }, [isOpen, showHistory, fetchDebates]);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isRunning || isDone) {
          onMinimize();
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isRunning, isDone, onMinimize, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleBackdropClick = useCallback(() => {
    if (isRunning || isDone) {
      onMinimize();
    } else {
      onClose();
    }
  }, [isRunning, isDone, onMinimize, onClose]);

  const handleStartDebate = useCallback(() => {
    if (!question.trim()) return;

    const params: DebateStartParams = {
      question: question.trim(),
      depth,
      mode,
      reference_debate_id: referenceDebateId || undefined,
    };

    // Attach page context if not dismissed
    if (pageContext && !contextDismissed) {
      params.context = {
        page_type: pageContext.pageType,
        tickers: pageContext.tickers,
        selected_ticker: pageContext.selectedTicker,
        portfolio_summary: pageContext.portfolioSummary as Record<string, unknown> | undefined,
        research_context: pageContext.researchContext,
      };
    }

    startDebate(params);
  }, [question, depth, mode, referenceDebateId, pageContext, contextDismissed, startDebate]);

  const handleReset = useCallback(() => {
    reset();
    setQuestion('');
    setContextDismissed(false);
    setReferenceDebateId(null);
  }, [reset]);

  const handleClose = useCallback(() => {
    disconnect();
    onClose();
  }, [disconnect, onClose]);

  // Build context chip text
  const contextChip = useMemo(() => {
    if (!pageContext || contextDismissed) return null;
    const parts: string[] = [];
    if (pageContext.selectedTicker) parts.push(pageContext.selectedTicker);
    else if (pageContext.tickers?.length) parts.push(pageContext.tickers.slice(0, 3).join(', '));
    if (pageContext.pageType === 'portfolio' && pageContext.portfolioSummary) {
      parts.push(`${pageContext.portfolioSummary.positionCount} positions`);
    }
    const source = pageContext.pageType.replace('-', ' ');
    return parts.length > 0 ? `${parts.join(' | ')} (from ${source})` : `Context from ${source}`;
  }, [pageContext, contextDismissed]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex flex-col',
          'w-full sm:w-[85%] md:w-[70%] lg:w-[60%] xl:w-[55%]',
          'bg-gradient-to-b from-[#0a0a0f] to-background border-l border-border shadow-2xl',
          'transition-transform duration-300 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <Swords className="w-5 h-5 text-verdict-primary" />
            <div>
              <h2 className="text-lg font-bold text-text-primary">Debate</h2>
              {state.status !== 'idle' && (
                <p className="text-xs text-text-muted mt-0.5">
                  {state.status === 'done'
                    ? `Completed in ${state.totalTimeSeconds.toFixed(0)}s`
                    : state.status === 'error'
                      ? 'Error'
                      : state.status === 'debating'
                        ? `Round ${state.currentRound}/${state.maxRounds}`
                        : state.status.replace('-', ' ')}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={cn(
                'p-2 rounded-lg transition-colors',
                showHistory
                  ? 'text-verdict-primary bg-verdict-primary/10'
                  : 'text-text-muted hover:text-text-primary hover:bg-surface-light',
              )}
              title="Past debates"
            >
              <History className="w-5 h-5" />
            </button>
            <button
              onClick={onMinimize}
              className="p-2 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-light transition-colors"
              title="Minimize"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg text-text-muted hover:text-red-400 hover:bg-red-400/10 transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {showHistory ? (
            <DebateHistoryPanel
              debates={debates}
              isLoading={historyLoading}
              onSelectReference={(id) => {
                setReferenceDebateId(id);
                setShowHistory(false);
              }}
              onViewDebate={(id) => {
                // TODO: Load and display full debate
                console.log('View debate:', id);
              }}
            />
          ) : state.status === 'idle' ? (
            <DebateInputPanel
              question={question}
              setQuestion={setQuestion}
              depth={depth}
              setDepth={setDepth}
              mode={mode}
              setMode={setMode}
              showModeToggle={showModeToggle}
              contextChip={contextChip}
              onDismissContext={() => setContextDismissed(true)}
              referenceDebateId={referenceDebateId}
              onClearReference={() => setReferenceDebateId(null)}
              onStart={handleStartDebate}
            />
          ) : (
            <DebateStreamPanel state={state} onReset={handleReset} />
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Input Panel (idle state)
// ============================================================================

function DebateInputPanel({
  question,
  setQuestion,
  depth,
  setDepth,
  mode,
  setMode,
  showModeToggle,
  contextChip,
  onDismissContext,
  referenceDebateId,
  onClearReference,
  onStart,
}: {
  question: string;
  setQuestion: (q: string) => void;
  depth: DepthOption;
  setDepth: (d: DepthOption) => void;
  mode: ModeOption;
  setMode: (m: ModeOption) => void;
  showModeToggle: boolean;
  contextChip: string | null;
  onDismissContext: () => void;
  referenceDebateId: string | null;
  onClearReference: () => void;
  onStart: () => void;
}) {
  return (
    <div className="p-6 space-y-6">
      {/* Question input */}
      <div>
        <label className="text-sm font-medium text-text-secondary mb-2 block">
          What should we debate?
        </label>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g., Is NVIDIA overvalued at current levels? / Should startups prioritize growth over profitability?"
          className="w-full bg-surface border border-border rounded-lg px-4 py-3 text-text-primary placeholder:text-text-muted resize-none focus:outline-none focus:ring-1 focus:ring-verdict-primary/50 min-h-[100px]"
          rows={4}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              onStart();
            }
          }}
        />
      </div>

      {/* Context chip */}
      {contextChip && (
        <div className="flex items-center gap-2 text-xs">
          <span className="bg-verdict-primary/10 text-verdict-primary px-3 py-1.5 rounded-full flex items-center gap-2">
            <MessageSquare className="w-3 h-3" />
            {contextChip}
            <button onClick={onDismissContext} className="hover:text-red-400 ml-1">
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}

      {/* Reference debate chip */}
      {referenceDebateId && (
        <div className="flex items-center gap-2 text-xs">
          <span className="bg-amber-500/10 text-amber-400 px-3 py-1.5 rounded-full flex items-center gap-2">
            <History className="w-3 h-3" />
            Referencing previous debate
            <button onClick={onClearReference} className="hover:text-red-400 ml-1">
              <X className="w-3 h-3" />
            </button>
          </span>
        </div>
      )}

      {/* Depth selector */}
      <div>
        <label className="text-sm font-medium text-text-secondary mb-2 block">Depth</label>
        <div className="grid grid-cols-3 gap-2">
          {(Object.keys(DEPTH_INFO) as DepthOption[]).map((d) => {
            const info = DEPTH_INFO[d];
            const Icon = info.icon;
            return (
              <button
                key={d}
                onClick={() => setDepth(d)}
                className={cn(
                  'p-4 rounded-xl border text-left transition-all',
                  depth === d
                    ? 'border-verdict-primary bg-verdict-primary/5 text-text-primary shadow-gold-sm'
                    : 'border-border hover:border-border-hover text-text-secondary',
                )}
              >
                <Icon className={cn('w-4 h-4 mb-2', depth === d ? 'text-verdict-primary' : 'text-text-muted')} />
                <div className="text-sm font-medium">{info.label}</div>
                <div className="text-xs text-text-muted mt-1">
                  {info.agents} agents, {info.rounds} rounds
                </div>
                <div className="text-xs text-text-muted">{info.credits} credits</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mode toggle (financial only) */}
      {showModeToggle && (
        <div>
          <label className="text-sm font-medium text-text-secondary mb-2 block">
            Debate Style
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setMode('adversarial')}
              className={cn(
                'p-3 rounded-lg border text-left transition-all',
                mode === 'adversarial'
                  ? 'border-verdict-primary bg-verdict-primary/5'
                  : 'border-border hover:border-border-hover',
              )}
            >
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <Swords className="w-4 h-4" />
                Adversarial
              </div>
              <div className="text-xs text-text-muted mt-1">Bull vs Bear teams</div>
            </button>
            <button
              onClick={() => setMode('multi-perspective')}
              className={cn(
                'p-3 rounded-lg border text-left transition-all',
                mode === 'multi-perspective'
                  ? 'border-verdict-primary bg-verdict-primary/5'
                  : 'border-border hover:border-border-hover',
              )}
            >
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <Users className="w-4 h-4" />
                Multi-Perspective
              </div>
              <div className="text-xs text-text-muted mt-1">Diverse viewpoints</div>
            </button>
          </div>
        </div>
      )}

      {/* Start button */}
      <button
        onClick={onStart}
        disabled={!question.trim()}
        className={cn(
          'w-full py-3 px-4 flex items-center justify-center gap-2',
          question.trim()
            ? 'btn-premium'
            : 'rounded-xl bg-surface text-text-muted cursor-not-allowed font-medium',
        )}
      >
        <Swords className="w-4 h-4" />
        Start Debate
      </button>

      <p className="text-xs text-text-muted text-center">
        Press Ctrl+Enter to start
      </p>
    </div>
  );
}

// ============================================================================
// Stream Panel (running/done state)
// ============================================================================

function DebateStreamPanel({ state, onReset }: { state: DebateStreamState; onReset: () => void }) {
  const [expandedRounds, setExpandedRounds] = useState<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new content arrives
  useEffect(() => {
    if (scrollRef.current && state.status !== 'done') {
      scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [state.arguments.length, state.mediatorChecks.length, state.status]);

  const toggleRound = (round: number) => {
    setExpandedRounds((prev) => {
      const next = new Set(prev);
      if (next.has(round)) next.delete(round);
      else next.add(round);
      return next;
    });
  };

  // Group arguments by round
  const argumentsByRound = useMemo(() => {
    const groups: Record<number, typeof state.arguments> = {};
    for (const arg of state.arguments) {
      const round = arg.roundNumber;
      if (!groups[round]) groups[round] = [];
      groups[round].push(arg);
    }
    return groups;
  }, [state.arguments]);

  const rounds = Object.keys(argumentsByRound)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className="p-6 space-y-4">
      {/* Status header */}
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        {state.status === 'done' ? (
          <CheckCircle2 className="w-5 h-5 text-green-400" />
        ) : state.status === 'error' ? (
          <AlertCircle className="w-5 h-5 text-red-400" />
        ) : (
          <Loader2 className="w-5 h-5 text-verdict-primary animate-spin" />
        )}
        <div className="flex-1">
          <p className="text-sm font-medium text-text-primary">
            {state.question}
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            {state.agents.length} agents | {state.depth} depth | {state.mode.replace('-', ' ')}
          </p>
        </div>
      </div>

      {/* Error message */}
      {state.error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 text-sm text-error">
          {state.error}
        </div>
      )}

      {/* Data gathering indicator */}
      {state.status === 'gathering-data' && (
        <div className="flex items-center gap-2 p-3 bg-surface rounded-lg border border-border">
          <Loader2 className="w-4 h-4 text-verdict-primary animate-spin shrink-0" />
          <span className="text-sm text-text-secondary">Gathering research data...</span>
        </div>
      )}

      {/* Data sources */}
      {state.dataSources.length > 0 && (
        <div className="text-xs text-text-muted">
          <span className="font-medium">Sources:</span>{' '}
          {state.dataSources.join(', ')}
        </div>
      )}

      {/* Agent chips */}
      {state.agents.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {state.agents.map((agent) => (
            <span
              key={agent.id}
              className="text-xs bg-surface border border-border rounded-full px-3 py-1 text-text-secondary"
            >
              {agent.role}
            </span>
          ))}
        </div>
      )}

      {/* Debate rounds */}
      {rounds.map((roundNum) => {
        const roundArgs = argumentsByRound[roundNum] || [];
        const mediatorCheck = state.mediatorChecks.find((m) => m.roundNumber === roundNum);
        const isExpanded = expandedRounds.has(roundNum) || roundNum === rounds[rounds.length - 1];
        const isInitialRound = roundNum === 0;

        return (
          <div key={roundNum} className="border border-border rounded-lg overflow-hidden">
            {/* Round header */}
            <button
              onClick={() => toggleRound(roundNum)}
              className="w-full flex items-center justify-between px-4 py-3 bg-surface hover:bg-surface-light transition-colors"
            >
              <span className="text-sm font-medium text-text-primary">
                {isInitialRound ? 'Initial Positions' : `Round ${roundNum}`}
              </span>
              <div className="flex items-center gap-3">
                {mediatorCheck && (
                  <span className="text-xs text-text-muted">
                    Agreement: {(mediatorCheck.agreementScore * 100).toFixed(0)}%
                  </span>
                )}
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-text-muted" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-text-muted" />
                )}
              </div>
            </button>

            {/* Round content */}
            {isExpanded && (
              <div className="px-4 py-3 space-y-3 border-t border-border">
                {roundArgs.map((arg, i) => (
                  <div key={`${arg.agentId}-${roundNum}-${i}`} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-verdict-primary">
                        {arg.agentRole}
                      </span>
                    </div>
                    <MarkdownContent content={arg.argument} className="text-sm text-text-secondary" />
                  </div>
                ))}

                {/* Mediator check */}
                {mediatorCheck && (
                  <div className="mt-3 pt-3 border-t border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <span className="text-xs font-semibold text-amber-400">Moderator</span>
                    </div>

                    {/* Agreement bar */}
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all duration-500',
                            mediatorCheck.agreementScore >= 0.8
                              ? 'bg-green-400'
                              : mediatorCheck.agreementScore >= 0.5
                                ? 'bg-amber-400'
                                : 'bg-red-400',
                          )}
                          style={{ width: `${mediatorCheck.agreementScore * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-text-muted min-w-[3ch]">
                        {(mediatorCheck.agreementScore * 100).toFixed(0)}%
                      </span>
                    </div>

                    {mediatorCheck.facilitationNote && (
                      <p className="text-xs text-amber-400/80 italic">
                        {mediatorCheck.facilitationNote}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Consensus / Final Synthesis */}
      {state.consensus && (
        <div className="border border-green-500/20 bg-green-500/5 rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-400" />
            <span className="text-sm font-semibold text-green-400">
              Debate Synthesis
            </span>
            <span className="text-xs text-text-muted ml-auto">
              Agreement: {(state.consensus.score * 100).toFixed(0)}%
            </span>
          </div>

          <MarkdownContent content={state.consensus.synthesis} className="text-sm text-text-secondary" />

          {state.consensus.resolved.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-text-muted">Resolved:</span>
              {state.consensus.resolved.map((point, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-text-secondary">
                  <CheckCircle2 className="w-3 h-3 text-green-400 mt-0.5 shrink-0" />
                  {point}
                </div>
              ))}
            </div>
          )}

          {state.consensus.unresolved.length > 0 && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-text-muted">Unresolved:</span>
              {state.consensus.unresolved.map((point, i) => (
                <div key={i} className="flex items-start gap-2 text-xs text-text-secondary">
                  <AlertCircle className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                  {point}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Done actions */}
      {(state.status === 'done' || state.status === 'error') && (
        <button
          onClick={onReset}
          className="w-full py-2.5 px-4 rounded-lg border border-border text-sm text-text-secondary hover:bg-surface-light transition-colors"
        >
          New Debate
        </button>
      )}

      {/* Scroll anchor */}
      <div ref={scrollRef} />
    </div>
  );
}

// ============================================================================
// History Panel
// ============================================================================

function DebateHistoryPanel({
  debates,
  isLoading,
  onSelectReference,
  onViewDebate: _onViewDebate,
}: {
  debates: DebateSummary[];
  isLoading: boolean;
  onSelectReference: (id: string) => void;
  onViewDebate: (id: string) => void;
}) {
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center text-text-muted">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading history...
      </div>
    );
  }

  if (debates.length === 0) {
    return (
      <div className="p-6 text-center text-text-muted">
        <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No past debates yet</p>
        <p className="text-xs mt-1">Start your first debate to build history</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      <p className="text-xs text-text-muted px-2 mb-3">
        Click to reference a past debate in your next one
      </p>
      {debates.map((debate) => (
        <div
          key={debate.id}
          className="p-3 rounded-lg border border-border hover:border-border-hover transition-colors cursor-pointer group"
          onClick={() => onSelectReference(debate.id)}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <TruncatedText text={debate.question} className="text-sm text-text-primary" />
              <div className="flex items-center gap-2 mt-1 text-xs text-text-muted">
                <span>{debate.depth}</span>
                <span>|</span>
                <span>{debate.agent_count} agents</span>
                <span>|</span>
                <span>{(debate.agreement_score * 100).toFixed(0)}% agreement</span>
              </div>
              {debate.tickers.length > 0 && (
                <div className="flex gap-1 mt-1">
                  {debate.tickers.map((t) => (
                    <span key={t} className="text-xs bg-surface px-1.5 py-0.5 rounded text-text-muted">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-text-muted" />
              <span className="text-xs text-text-muted whitespace-nowrap">
                {formatRelativeDate(debate.created_at)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Minimized Pill
// ============================================================================

export function DebateMinimizedPill({
  state,
  onClick,
}: {
  state: DebateStreamState;
  onClick: () => void;
}) {
  // Defensive: parent should never mount this when idle, but guard just in case
  if (state.status === 'idle') return null;

  return (
    <button
      onClick={onClick}
      className={cn(
        'fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-2.5 rounded-full',
        'shadow-lg border transition-all hover:scale-105',
        state.status === 'done'
          ? 'bg-green-500/10 border-green-500/30 text-green-400'
          : state.status === 'error'
            ? 'bg-red-500/10 border-red-500/30 text-red-400'
            : 'bg-verdict-primary/10 border-verdict-primary/30 text-verdict-primary',
      )}
    >
      {state.status === 'done' ? (
        <CheckCircle2 className="w-4 h-4" />
      ) : state.status === 'error' ? (
        <AlertCircle className="w-4 h-4" />
      ) : (
        <Loader2 className="w-4 h-4 animate-spin" />
      )}
      <span className="text-sm font-medium">
        {state.status === 'done'
          ? 'Debate complete'
          : state.status === 'error'
            ? 'Debate error'
            : state.status === 'debating'
              ? `Debating... Round ${state.currentRound}/${state.maxRounds}`
              : 'Debate in progress...'}
      </span>
    </button>
  );
}

// ============================================================================
// Trigger FAB
// ============================================================================

export function DebateTriggerFAB({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'fixed bottom-6 right-6 z-30',
        'flex items-center gap-2 px-4 py-2.5 rounded-full',
        'bg-verdict-primary/10 border border-verdict-primary/30',
        'text-verdict-primary hover:bg-verdict-primary/20',
        'shadow-lg transition-all hover:scale-105',
      )}
      title="Open Debate Panel (Ctrl+Shift+D)"
    >
      <Swords className="w-4 h-4" />
      <span className="text-sm font-medium hidden sm:inline">Debate</span>
    </button>
  );
}

// ============================================================================
// Utils
// ============================================================================

function formatRelativeDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  } catch {
    return dateStr;
  }
}
